import React from "react";
import { MarkerType } from "reactflow";
import type { SelectOption, Variable, EdgeConfig, ToolbarConfig } from "../types";

export const STARTING_NODE_ID = "starting-node" as const;
export const STARTING_NODE_ID2 = "starting-node-2" as const;

export const NODE_TYPE = {
    STARTING_NODE: "startingNode",
    MESSAGE_NODE: "messageNode",
    COLLECT_INPUT_NODE: "collectInputNode",
    BUTTONS_NODE: "buttonsNode",
    BUTTON_NODE: "buttonNode",
    FILE_UPLOAD_NODE: "fileUploadNode",
    CALENDAR_NODE: "calendarNode",
    CODE_BLOCK_NODE: "codeBlockNode",
    SERVICE_CALL_NODE: "serviceCallNode",
    CARD_NODE: "cardNode",
    CONDITIONAL_NODE: "conditionalNode",
    JUMP_TO_NODE: "jumpToNode",
    END_NODE: "endNode",
    EMAIL_NODE: "emailNode",
    JUMP_TO_PROFILE_NODE: "jumpToProfileNode",
} as const;

export type NodeTypeKey = keyof typeof NODE_TYPE;
export type NodeTypeValue = typeof NODE_TYPE[NodeTypeKey];

export const BUTTON_TYPE = {
    BRANCH_BUTTON: "branchButton",
    URL_BUTTON: "linkButton",
} as const;

export type ButtonTypeKey = keyof typeof BUTTON_TYPE;
export type ButtonTypeValue = typeof BUTTON_TYPE[ButtonTypeKey];

export interface StartingNodePosition {
    id: string;
    type: typeof NODE_TYPE.STARTING_NODE;
    position: { x: number; y: number };
    data: {
        startingText: string;
        isBottom: boolean;
        type: typeof NODE_TYPE.STARTING_NODE;
    };
}

export const STARTING_NODE_POSITION: StartingNodePosition = {
    id: STARTING_NODE_ID,
    type: NODE_TYPE.STARTING_NODE,
    position: { x: typeof window !== 'undefined' ? window.innerWidth / 4 - 140 : 220, y: 100 },
    data: {
        startingText: "Invite Link",
        isBottom: true,
        type: NODE_TYPE.STARTING_NODE,
    },
};

export const STARTING_NODE_POSITION2: StartingNodePosition = {
    id: STARTING_NODE_ID2,
    type: NODE_TYPE.STARTING_NODE,
    position: { x: typeof window !== 'undefined' ? window.innerWidth / 1.5 - 140 : 820, y: 100 },
    data: {
        startingText: "Apply Link",
        isBottom: true,
        type: NODE_TYPE.STARTING_NODE,
    },
};

export const options: SelectOption[] = [
    { label: "contact_created_by", value: "contact_created_by" },
    { label: "df", value: "df" },
    { label: "email", value: "email" },
    { label: "gcal_email", value: "gcal_email" },
    { label: "gcal_name", value: "gcal_name" },
    { label: "name", value: "name" },
    { label: "phone", value: "phone" },
];

export const ALLOWED_FILE_TYPES: string[] = [".jpg", ".png", ".pdf", ".doc", ".docx"];

export type InputSubType = "text" | "number" | "date" | "email" | "regex" | "address";

export const inputSubTypeOptions: SelectOption<InputSubType>[] = [
    { label: "Text", value: "text" },
    { label: "Number", value: "number" },
    { label: "Date", value: "date" },
    { label: "Email", value: "email" },
    { label: "Regular Expression", value: "regex" },
];

export type EmailType = "email" | "sms";

export const emailTypeOptions: SelectOption<EmailType>[] = [
    { label: "Email", value: "email" },
    { label: "SMS", value: "sms" },
];

export const nodeTypes: SelectOption<string>[] = [
    { label: "Send Message", value: NODE_TYPE.MESSAGE_NODE },
    { label: "Ask a Text Question", value: NODE_TYPE.COLLECT_INPUT_NODE },
    { label: "Ask a Choice Question", value: NODE_TYPE.BUTTON_NODE },
    { label: "Collect Document", value: NODE_TYPE.FILE_UPLOAD_NODE },
    { label: "Code Block", value: NODE_TYPE.CODE_BLOCK_NODE },
    { label: "Service Call", value: NODE_TYPE.SERVICE_CALL_NODE },
    { label: "Carousel", value: NODE_TYPE.CARD_NODE },
    { label: "Conditional Block", value: NODE_TYPE.CONDITIONAL_NODE },
    { label: "Jump to Node", value: NODE_TYPE.JUMP_TO_NODE },
    { label: "End Chat", value: NODE_TYPE.END_NODE },
];

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH";

export const servceCallTypes: SelectOption<HttpMethod>[] = [
    { label: "GET", value: "GET" },
    { label: "POST", value: "POST" },
    { label: "PUT", value: "PUT" },
    { label: "PATCH", value: "PATCH" },
];

export type BodyType = "raw" | "form";

export const bodyTypes: SelectOption<BodyType>[] = [
    { label: "Raw", value: "raw" },
    { label: "Form", value: "form" },
];

export type DateFormat = "DD/MM/YYYY" | "DD-MM-YYYY" | "YYYY/MM/DD" | "YYYY-MM-DD";

export const dateFormatOptions: SelectOption<DateFormat>[] = [
    { label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
    { label: "DD-MM-YYYY", value: "DD-MM-YYYY" },
    { label: "YYYY/MM/DD", value: "YYYY/MM/DD" },
    { label: "YYYY-MM-DD", value: "YYYY-MM-DD" },
];

export type AssessmentValue = "Shortlisted" | "Maybe" | "Rejected";

export const buttonOptions: SelectOption<AssessmentValue>[] = [
    { label: "Shortlisted", value: "Shortlisted" },
    { label: "Maybe", value: "Maybe" },
    { label: "Rejected", value: "Rejected" },
];

export type MappingType = "FunctionExpertise" | "SubFunctionExpertise" | "Skills" | "Industry" | "SubIndustry" | "CandidateStatus" | "ApplicationSubStatus" | "ApplicationStage";

export type PlatformType = "VINCERE" | "FASTTRACK" | "RDB" | "ARCRM" | "SALESFORCE" | "JOBADDER";

export interface MappingOption {
    label: string;
    value: MappingType;
    platformType: PlatformType;
    dataField: string;
    placeholder: string;
    searchPlaceholder: string;
    isMultiSelect?: boolean;
    screeningOnly?: boolean;
}

export const mappingOptions: MappingOption[] = [
    {
        label: "Function Expertise",
        value: "FunctionExpertise",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Function Expertise",
        searchPlaceholder: "Search Function Expertise...",
    },
    {
        label: "Sub Function Expertise",
        value: "SubFunctionExpertise",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Sub Function Expertise",
        searchPlaceholder: "Search Sub Function Expertise...",
    },
    {
        label: "Industry",
        value: "Industry",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Industry",
        searchPlaceholder: "Search Industry...",
    },
    {
        label: "Sub Industry",
        value: "SubIndustry",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Sub Industry",
        searchPlaceholder: "Search Sub Industry...",
    },
    {
        label: "Skills",
        value: "Skills",
        platformType: "FASTTRACK",
        dataField: "optionValue",
        placeholder: "Select Skills",
        searchPlaceholder: "Search Skills...",
    },
    {
        label: "Skills",
        value: "Skills",
        platformType: "JOBADDER",
        dataField: "optionValue",
        placeholder: "Select Skills",
        searchPlaceholder: "Search Skills...",
    },
    {
        label: "Candidate Status",
        value: "CandidateStatus",
        platformType: "FASTTRACK",
        dataField: "optionValue",
        placeholder: "Select Candidate Status",
        searchPlaceholder: "Search Candidate Status...",
        isMultiSelect: false,
    },
    {
        label: "Candidate Status",
        value: "CandidateStatus",
        platformType: "JOBADDER",
        dataField: "optionValue",
        placeholder: "Select Candidate Status",
        searchPlaceholder: "Search Candidate Status...",
        isMultiSelect: false,
    },
    {
        label: "Candidate Status",
        value: "CandidateStatus",
        platformType: "ARCRM",
        dataField: "optionValue",
        placeholder: "Select Candidate Status",
        searchPlaceholder: "Search Candidate Status...",
        isMultiSelect: false,
    },
    {
        label: "Candidate Status",
        value: "CandidateStatus",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Candidate Status",
        searchPlaceholder: "Search Candidate Status...",
        isMultiSelect: false,
    },
    {
        label: "Application Sub-Status",
        value: "ApplicationSubStatus",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Application Sub-Status",
        searchPlaceholder: "Search Application Sub-Status...",
        isMultiSelect: false,
        screeningOnly: true,
    },
    {
        label: "Application Stage",
        value: "ApplicationStage",
        platformType: "VINCERE",
        dataField: "optionValue",
        placeholder: "Select Application Stage",
        searchPlaceholder: "Search Application Stage...",
        isMultiSelect: false,
        screeningOnly: true,
    },
];

export interface MappingLookupItem {
    mappingType: MappingType;
    key: string;
    value: string;
    group?: string;
}

export const mappingLookupData: MappingLookupItem[] = [];

export type ConditionOperator =
    | "equals to"
    | "not equals to"
    | "contains"
    | "does not contains"
    | "Equals one of"
    | "Does not equals one of"
    | "is empty"
    | "is not empty"
    | "is greater than"
    | "is less than"
    | "choices selected greater than"
    | "choices selected less than";

export const conditionOptions: SelectOption<ConditionOperator>[] = [
    { label: "equals to", value: "equals to" },
    { label: "not equals to", value: "not equals to" },
    { label: "contains", value: "contains" },
    { label: "does not contains", value: "does not contains" },
    { label: "equals one of", value: "Equals one of" },
    { label: "does not equals one of", value: "Does not equals one of" },
    { label: "is empty", value: "is empty" },
    { label: "is not empty", value: "is not empty" },
    { label: "is greater than", value: "is greater than" },
    { label: "is less than", value: "is less than" },
    {
        label: "choices selected greater than",
        value: "choices selected greater than",
    },
    {
        label: "choices selected less than",
        value: "choices selected less than",
    }
];

export const edgeConfig: EdgeConfig = {
    markerEnd: {
        type: MarkerType.Arrow,
        color: "#bde8e7",
    },
    markerStart: {
        type: MarkerType.Arrow,
        color: "#bde8e7",
        orient: "auto-start-reverse",
    },
    strokeWidth: 2,
    className: "custom-edge-class",
};

export const toolbar: ToolbarConfig = {
    options: ["inline", "list", "link"],
    inline: {
        options: ["bold", "italic", "underline"],
    },
    list: {
        options: ["unordered", "ordered"],
    },
    link: {
        popupClassName: "link-popup-class"
    }
};

export const emptyToolbar: ToolbarConfig = {
    options: [],
    inline: {},
    list: {},
};

export interface VariableDefinition {
    label: string;
    value: string;
    profileIds: number[];
}

export const VAR_DEFAULT_VARIABLE_LIST: VariableDefinition[] = [
    {
        label: "ApplicationId",
        value: "{applicationId}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate FirstName",
        value: "{firstName}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate LastName",
        value: "{lastName}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate EmailId",
        value: "{emailId}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate Mobile",
        value: "{mobile}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate Suburb",
        value: "{suburb}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate State",
        value: "{state}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate PostCode",
        value: "{postCode}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate Address",
        value: "{address}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate Id",
        value: "{hostSystemUserId}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate Current Job Title",
        value: "{jobTitle}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Candidate Current Employer",
        value: "{currentEmployer}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Job Position",
        value: "{position}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Job Location",
        value: "{jobLocation}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "JobOrder Id",
        value: "{jobOrderId}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "JobOrder Expired ",
        value: "{isJobOrderExpired}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Interview Completed",
        value: "{interviewCompleted}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "BotName",
        value: "{botName}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Bot AdminEmail",
        value: "{botAdminEmail}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Company Name",
        value: "{companyName}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Invite Source",
        value: "{inviteSource}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Screening Assessment",
        value: "{Assessment}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Custom QuestionOne",
        value: "{questionOne}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Custom QuestionTwo",
        value: "{questionTwo}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Custom QuestionThree",
        value: "{questionThree}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Custom QuestionFour",
        value: "{questionFour}}",
        profileIds: [36, 37, 83, 89]
    },
    {
        label: "Campaign Notification Email",
        value: "{campaignNotificationEmail}}",
        profileIds: [37]
    }
];

export const VAR_DEFAULT_FASTTRACK_ADD_DOCUMENT_TO: SelectOption[] = [
    {
        label: "Document Management",
        value: "Candidate",
    },
    {
        label: "Candidate Resume",
        value: "Candidate Resume",
    }
];

export const VAR_DEFAULT_RDB_ADD_DOCUMENT_TO: SelectOption[] = [
    {
        label: "Stored Documents",
        value: "STOREDDOCUMENTS",
    },
    {
        label: "CV",
        value: "CV",
    }
];

export const VAR_DEFAULT_SALESFORCE_ADD_DOCUMENT_TO: SelectOption[] = [
    {
        label: "Files",
        value: "FILES",
    }
];

export const DEFAULT_VARIABLE_LIST: Variable[] = VAR_DEFAULT_VARIABLE_LIST?.map((item) => ({
    ...item,
    text: (
        <>
            <span className="var-text">{item.label}</span>
            <span className="var-type">System</span>
        </>
    ),
}));

export const EMPTY_DRAFT = "<p></p>" as const;

export const INITIAL_GAP_BETWEEN_TWO_NODES = 175 as const;
